import React from "react";
import { INormalizedProduct } from "../../models/NormalizedProduct";
import { IVendor } from "../../models/Search";
import { WarehouseTable } from "./WarehouseTable";
import Moment from "moment";
import { Callout, DirectionalHint } from "@fluentui/react";
import { useId } from '@fluentui/react-hooks';
import AddToCollectionButton from "../ProductDetails/AddToCollectionButton";
import VendorPeek from "./VendorPeek";

interface IInStockDisplayProps {
    product: INormalizedProduct;
    multiLine?: boolean;
    size?: "big" | "small" | "mini" | "tiny" | "medium" | "large" | "huge" | "massive" | undefined;
    loadingWarehouses: boolean;
    vendorName?: string;
    refreshing?: boolean;
    fluid?: boolean;
    hideWarehouses?: boolean;
    vendor?: IVendor;
    isExample?: boolean;
}

export const InStockDisplay: React.FC<IInStockDisplayProps> = (props) => {
    const [open, setOpen] = React.useState(false);
    const id = useId('stock-button');

    let totalStock = 0;
    const stockDisplay = props.loadingWarehouses
        ? <p>Loading</p>
        : <WarehouseTable product={props.product} vendor={props.vendorName} />;
    let inStockDate: Date | undefined = undefined;
    let inStock = false;
    if (props.vendor) {
        if (props.vendor.inStockDate) {
            inStockDate = props.vendor.inStockDate;
        }
        if (props.vendor.onHand != null && props.vendor.onHand > 0) {
            totalStock += props.vendor.onHand;
        }
        inStock = props.vendor.inStock || (props.vendor.onHand != null && props.vendor.onHand > 0);
    } else {
        if (props.product.vendors) {
            props.product.vendors.forEach((vendor: IVendor) => {
                if (!props.vendorName || vendor.vendorName === props.vendorName) {
                    if (vendor.inStockDate &&
                        (inStockDate === undefined || vendor.inStockDate < inStockDate)
                    ) {
                        inStockDate = vendor.inStockDate;
                    }
                    if (vendor.onHand != null && vendor.onHand > 0) {
                        totalStock += vendor.onHand;
                    }
                }
            });
        }
        if (props.vendorName) {
            const vendor = props.product.vendors?.find((v) => v.vendorName === props.vendorName);
            if (vendor) {
                inStock = vendor.inStock || (vendor.onHand != null && vendor.onHand > 0);
            }
        } else if (props.product.stock) {
            inStock = true;
        }
    }
    if (inStock) {
        return (
            <div className="in-stock-dense">
                {/* The peek sits inside the filled element: the cell's fill keys off in-stock-dense being its direct child. */}
                <VendorPeek product={props.product} highlight={props.vendorName ?? props.vendor?.vendorName} isExample={props.isExample}>
                    <div
                        id={id}
                        onClick={() => setOpen(true)}
                        className='figure-cell'
                        style={{cursor: 'pointer'}}
                        >
                        <span className='figure-top'>Stock</span>
                        <span className='figure-main'>{totalStock > 0 ? totalStock : 'In stock'}</span>
                    </div>
                </VendorPeek>
                {(open && !props.hideWarehouses) && (
                    <Callout
                        style={{
                            padding: '1em',
                        }}
                        directionalHint={DirectionalHint.leftCenter}
                        target={`#${id}`}
                        onDismiss={() => setOpen(false)}
                        role='status'
                        >
                            {stockDisplay}
                            <AddToCollectionButton
                                product={props.product}
                                source={props.vendor?.vendorName}
                                onChange={() => setOpen(false)}
                                priceLevel={props.vendor?.priceLevelPartNumber}
                                isExample={props.isExample}
                            />
                        </Callout>
                )}
            </div>
        );
    }
    const inStockDateDisplay =
        inStockDate !== undefined
        ? <>Estimated: {props.multiLine ? <br /> : null }{Moment(inStockDate).format("MM-DD-YY")}</>
        : null;
    // Same three-slot layout as the in-stock cell, so "No Stock" centers exactly where a stock count would.
    return <div className="no-stock-dense" style={{color: props.refreshing ? 'grey' : '#dd1919ff', width: '100%'}}>
        <VendorPeek product={props.product} highlight={props.vendorName ?? props.vendor?.vendorName} isExample={props.isExample}>
            <div className='figure-cell'>
                <span className='figure-top' />
                <span className='figure-main' style={{fontWeight: 600, whiteSpace: 'normal', textAlign: 'center'}}>
                    {inStockDateDisplay ? inStockDateDisplay : "No Stock" }
                </span>
            </div>
        </VendorPeek>
    </div>;
};
